import {useCallback,useEffect,useMemo,useRef} from 'react';

/** Poll persisted deployment progress only when the server advertises the capability.
 * Every callback belongs to one session/capability generation, including A→B→A. */
export function useRailwayHistory<T>(sessionId:string|undefined,enabled:boolean,
  read:(id:string)=>Promise<T[]>,apply:(rows:T[])=>void,pending:(rows:T[])=>boolean) {
  const callbacks=useRef({read,apply,pending});callbacks.current={read,apply,pending};
  const watch=useMemo(()=>({active:true,timer:null as ReturnType<typeof setInterval>|null,deadline:0,busy:false,lastRead:0}),[sessionId,enabled]);
  useEffect(()=>{
    watch.active=true;
    return ()=>{watch.active=false;if(watch.timer)clearInterval(watch.timer);watch.timer=null;};
  },[watch]);
  return useCallback(async function refresh(messages?:T[]):Promise<void>{
    if(!sessionId||!watch.active||(!messages&&(document.hidden||!enabled)))return;
    if(!messages&&watch.deadline&&Date.now()>=watch.deadline)return;
    if(watch.busy||(!messages&&Date.now()-watch.lastRead<10_000))return;
    watch.busy=true;watch.lastRead=Date.now();
    try{
      const rows=messages??await callbacks.current.read(sessionId);
      if(!watch.active)return;
      callbacks.current.apply(rows);
      if(enabled&&callbacks.current.pending(rows)){
        if(!watch.deadline)watch.deadline=Date.now()+30*60_000;
        if(!watch.timer&&Date.now()<watch.deadline)watch.timer=setInterval(()=>{
          if(Date.now()>=watch.deadline){if(watch.timer)clearInterval(watch.timer);watch.timer=null;return;}
          void refresh();
        },10_000);
      }else{if(watch.timer)clearInterval(watch.timer);watch.timer=null;watch.deadline=0;}
    }catch{/* Preserve existing progress during transient history read failures. */}
    finally{watch.busy=false;}
  },[sessionId,enabled,watch]);
}
